import { useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useGetRallyById,
  useGetRallyCompetitors,
  useGetRallyStages,
  useGetRallies,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import {
  useGetRankingsByEvent,
  useSetRankingsMutation,
} from "../../../../../api/jeep-rally/rankings/rankings.hooks";
import { useGetTeamsByEvent } from "../../../../../api/jeep-rally/teams/teams.hooks";
import { useGetAllTypes } from "../../../../../api/jeep-rally/types/types.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import {
  getCategorySelectValue,
  getRallyCategories,
  normalizeRallies,
  normalizeRally,
} from "../../Rallies/utils/rally.utils";
import { normalizeTypes } from "../../Types/utils/type.utils";
import RankingForm from "../_components/RankingForm";
import {
  buildRankingPayload,
  buildRankingSheetRows,
  buildSampleRankingRows,
  downloadRankingsWorkbook,
  emptyRankingRow,
  filterCategoriesByType,
  getCompetitorId,
  getRankingApiErrorMessage,
  initialRankingForm,
  mapImportedRowsToRankings,
  mapRankingsToFormRows,
  normalizeCompetitors,
  normalizeRankings,
  normalizeStages,
  parseRankingsImportFile,
  resolveRallyTypes,
  teamMatchesTypeCategory,
  validateRankingForm,
} from "../utils/ranking.utils";

const RankingCreatePage = () => {
  const history = useHistory();
  const location = useLocation();
  const canManage = isJeepRallyAdmin();
  const prefillKeyRef = useRef("");

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const eventIdFromQuery = query.get("eventId") || "";
  const typeFromQuery = query.get("type") || "";
  const categoryFromQuery = query.get("category") || "";
  const stageFromQuery = query.get("after_stage") || "";

  const [eventId, setEventId] = useState(eventIdFromQuery);
  const [form, setForm] = useState(() => ({
    ...initialRankingForm,
    type: typeFromQuery,
    category: categoryFromQuery,
    after_stage: stageFromQuery,
    rankings: [emptyRankingRow()],
  }));
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [importMessage, setImportMessage] = useState("");
  const [importError, setImportError] = useState("");
  const [userEditedRows, setUserEditedRows] = useState(false);

  const { data: ralliesData, isLoading: isRalliesLoading } = useGetRallies();
  const rallies = useMemo(() => normalizeRallies(ralliesData), [ralliesData]);

  useEffect(() => {
    if (eventIdFromQuery) setEventId(eventIdFromQuery);
  }, [eventIdFromQuery]);

  const {
    data: rallyData,
    isLoading: isRallyLoading,
  } = useGetRallyById(eventId);

  const { data: typesData, isLoading: isTypesLoading } = useGetAllTypes({
    status: "active",
  });
  const allTypes = useMemo(() => normalizeTypes(typesData), [typesData]);

  const { data: allCategoriesData, isLoading: isAllCategoriesLoading } =
    useGetAllCategories();

  const allCategories = useMemo(
    () => normalizeCategories(allCategoriesData),
    [allCategoriesData]
  );

  const rally = useMemo(() => normalizeRally(rallyData), [rallyData]);

  const types = useMemo(
    () => resolveRallyTypes(rally, allTypes),
    [rally, allTypes]
  );

  const rallyCategories = useMemo(() => {
    const rallyCats = getRallyCategories(rally);
    if (!rallyCats.length) return allCategories;

    return rallyCats
      .map((category) => {
        if (category && typeof category === "object" && category.key) {
          return category;
        }
        const key =
          typeof category === "string"
            ? category
            : getCategorySelectValue(category);
        const match = allCategories.find(
          (item) =>
            item.key === key || item._id === key || item.id === key
        );
        return match || { key, title: key };
      })
      .filter((category) => category?.key || category?._id || category?.id);
  }, [rally, allCategories]);

  const categories = useMemo(
    () => filterCategoriesByType(rallyCategories, form.type),
    [rallyCategories, form.type]
  );

  const {
    data: stagesData,
    isLoading: isStagesLoading,
  } = useGetRallyStages(eventId, {
    enabled: Boolean(eventId),
  });

  const stages = useMemo(() => normalizeStages(stagesData), [stagesData]);

  const canLoadTeams = Boolean(eventId) && Boolean(form.type) && Boolean(form.category);

  const {
    data: competitorsData,
    isLoading: isCompetitorsLoading,
  } = useGetRallyCompetitors(
    eventId,
    { type: form.type, category_id: form.category },
    { enabled: canLoadTeams }
  );

  const {
    data: teamsData,
    isLoading: isTeamsLoading,
  } = useGetTeamsByEvent(
    eventId,
    { type: form.type, category: form.category },
    { enabled: canLoadTeams }
  );

  const competitors = useMemo(() => {
    const fromCompetitors = normalizeCompetitors(competitorsData);
    const fromTeams = normalizeCompetitors(teamsData);
    const merged = [...fromCompetitors, ...fromTeams];
    const byId = new Map();

    merged.forEach((item) => {
      const id = getCompetitorId(item);
      if (!id) return;
      if (!teamMatchesTypeCategory(item, form.type, form.category)) return;
      byId.set(id, { ...(byId.get(id) || {}), ...item });
    });

    return Array.from(byId.values());
  }, [competitorsData, teamsData, form.type, form.category]);

  const canPrefill =
    Boolean(eventId) &&
    Boolean(form.type) &&
    Boolean(form.category) &&
    form.after_stage !== "";

  const {
    data: existingRankingsData,
    isFetching: isPrefilling,
  } = useGetRankingsByEvent(
    eventId,
    {
      type: form.type,
      categoryId: form.category,
      after_stage: form.after_stage === "" ? undefined : Number(form.after_stage),
    },
    { enabled: canPrefill && !userEditedRows }
  );

  useEffect(() => {
    if (!canPrefill || userEditedRows) return;

    const key = `${eventId}|${form.type}|${form.category}|${form.after_stage}`;
    if (prefillKeyRef.current === key) return;

    const existing = normalizeRankings(existingRankingsData);
    if (!existing.length && isPrefilling) return;

    prefillKeyRef.current = key;
    if (existing.length) {
      setForm((prev) => ({
        ...prev,
        rankings: mapRankingsToFormRows(existing),
      }));
      setImportMessage(
        `Loaded ${existing.length} existing row${
          existing.length === 1 ? "" : "s"
        } for this snapshot. Edit and save to upsert.`
      );
    }
  }, [
    canPrefill,
    userEditedRows,
    eventId,
    form.type,
    form.category,
    form.after_stage,
    existingRankingsData,
    isPrefilling,
  ]);

  const {
    mutateAsync: setRankings,
    isLoading,
    isPending,
    reset,
  } = useSetRankingsMutation();

  const submitting = isLoading || isPending;
  const rallyName =
    rally?.name ||
    rallies.find((item) => (item._id || item.id) === eventId)?.name ||
    "";

  const fileBaseName = [
    "rankings",
    rallyName || "rally",
    form.type || "type",
    form.category || "category",
    form.after_stage !== "" ? `stage-${form.after_stage}` : null,
  ]
    .filter(Boolean)
    .join("-")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .toLowerCase();

  const updateField = (key, value) => {
    if (key === "type" || key === "category" || key === "after_stage") {
      setUserEditedRows(false);
      prefillKeyRef.current = "";
      setImportMessage("");
    }

    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "type") {
        next.category = "";
        next.rankings = [emptyRankingRow()];
      }
      if (key === "category") {
        next.rankings = [emptyRankingRow()];
      }
      return next;
    });
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
    setImportError("");
  };

  const updateRow = (index, patch) => {
    setUserEditedRows(true);
    setForm((prev) => {
      const rankings = [...(prev.rankings || [])];
      rankings[index] = { ...rankings[index], ...patch };
      return { ...prev, rankings };
    });
    setValidationErrors((prev) => {
      if (!prev.rows?.[index]) return prev;
      const nextRows = { ...prev.rows };
      nextRows[index] = { ...nextRows[index] };
      Object.keys(patch).forEach((field) => {
        delete nextRows[index][field];
      });
      if (!Object.keys(nextRows[index]).length) delete nextRows[index];
      return { ...prev, rows: nextRows };
    });
  };

  const addRow = () => {
    setUserEditedRows(true);
    setForm((prev) => ({
      ...prev,
      rankings: [...(prev.rankings || []), emptyRankingRow()],
    }));
  };

  const removeRow = (index) => {
    setUserEditedRows(true);
    setForm((prev) => {
      const rankings = (prev.rankings || []).filter((_, i) => i !== index);
      return {
        ...prev,
        rankings: rankings.length ? rankings : [emptyRankingRow()],
      };
    });
  };

  const handleDownloadSample = () => {
    downloadRankingsWorkbook({
      rows: buildSampleRankingRows(competitors),
      filename: `${fileBaseName}-sample`,
      format: "xlsx",
    });
  };

  const handleExport = () => {
    downloadRankingsWorkbook({
      rows: buildRankingSheetRows(form.rankings, competitors),
      filename: fileBaseName,
      format: "xlsx",
    });
  };

  const handleImportFile = async (file) => {
    setImportMessage("");
    setImportError("");
    setUserEditedRows(true);
    setValidationErrors((prev) => ({
      ...prev,
      rankings: undefined,
      rows: undefined,
    }));

    try {
      const importedRows = await parseRankingsImportFile(file);
      const { rankings, warnings } = mapImportedRowsToRankings(
        importedRows,
        competitors
      );

      if (!rankings.length) {
        setImportError(
          "No ranking rows found in the file. Use the sample template headers."
        );
        return;
      }

      setForm((prev) => ({
        ...prev,
        rankings,
      }));

      if (warnings.length) {
        setImportError(warnings.slice(0, 5).join(" "));
      }
      setImportMessage(
        `Imported ${rankings.length} row${
          rankings.length === 1 ? "" : "s"
        } from ${file.name}. Review and edit before saving.`
      );
    } catch (error) {
      setImportError(
        error?.message || "Failed to import file. Use .xlsx or .csv."
      );
    }
  };

  const goBack = () => {
    const search = new URLSearchParams();
    if (eventId) search.set("eventId", eventId);
    if (form.type) search.set("type", form.type);
    if (form.category) search.set("category", form.category);
    if (form.after_stage !== "") search.set("after_stage", form.after_stage);
    const queryString = search.toString();
    history.push(
      queryString
        ? `/jeep-rally/ranking?${queryString}`
        : "/jeep-rally/ranking"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage || !eventId) return;

    reset();
    setSubmitError("");
    setImportMessage("");

    const nextErrors = validateRankingForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await setRankings({
        eventId,
        payload: buildRankingPayload(form),
      });
      const search = new URLSearchParams({
        eventId,
        type: form.type,
        category: form.category,
        after_stage: String(form.after_stage),
      });
      history.push(`/jeep-rally/ranking?${search.toString()}`);
    } catch (error) {
      setSubmitError(
        getRankingApiErrorMessage(error, "Failed to save rankings.")
      );
    }
  };

  if (!eventId) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Add rankings</h4>
          </div>
          <div className="card-body">
            <Alert variant="warning" className="mb-3">
              Select a rally on the rankings list first, then click Add / Edit
              rankings.
            </Alert>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => history.push("/jeep-rally/ranking")}
            >
              Back to rankings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rankings list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Add / Edit rankings</h4>
          </div>
        </div>

        <div className="card-body">
          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to set rankings.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          {(isRalliesLoading || isRallyLoading) && !rallyName ? (
            <p className="text-muted">Loading rally...</p>
          ) : null}

          <RankingForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            readOnly={!canManage}
            types={types}
            isTypesLoading={isRallyLoading || isTypesLoading}
            categories={categories}
            isCategoriesLoading={isRallyLoading || isAllCategoriesLoading}
            stages={stages}
            isStagesLoading={isStagesLoading}
            competitors={competitors}
            isCompetitorsLoading={isCompetitorsLoading || isTeamsLoading}
            rallyName={rallyName}
            importMessage={importMessage}
            importError={importError}
            isPrefilling={canPrefill && isPrefilling && !userEditedRows}
            submitLabel="Save rankings"
            submittingLabel="Saving..."
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onRowChange={updateRow}
            onAddRow={addRow}
            onRemoveRow={removeRow}
            onDownloadSample={handleDownloadSample}
            onExport={handleExport}
            onImportFile={handleImportFile}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default RankingCreatePage;
